// client/src/components/chat/ChatPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { io } from 'socket.io-client';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../../axiosInstance';
import AppBarHeader from '../AppBarHeader';
import RoomList from './RoomList';
import RoomPanel from './RoomPanel';
import './retro-chat.css';

function getSocketURL() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:5000';
  if (window.location.protocol === 'https:') return `https://${window.location.host}`;
  return window.location.origin;
}

export default function ChatPage() {
  const token = localStorage.getItem('token');
  const currentUser = token ? (() => { try { return jwtDecode(token); } catch { return null; } })() : null;
  const currentUserId = currentUser?.id || null;
  const currentUsername = currentUser?.username || 'Guest';
  const isAdmin = currentUser?.isAdmin || false;
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };
  const [searchParams] = useSearchParams();
  const targetRoom = searchParams.get('room');

  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const socketRef = useRef(null);
  const joinedRoomsRef = useRef(new Set());
  const activeRoomRef = useRef(activeRoom);

  useEffect(() => {
    activeRoomRef.current = activeRoom;
  }, [activeRoom]);

  useEffect(() => {
    if (!token) return;
    const socket = io(getSocketURL(), {
      path: '/socket.io/',
      transports: ['polling', 'websocket'],
      auth: { token },
    });
    socketRef.current = socket;

    socket.on('chat-history', ({ room, messages: msgs }) => {
      setMessages(prev => ({ ...prev, [room]: msgs }));
    });

    socket.on('chat-message', ({ room, message }) => {
      setMessages(prev => ({
        ...prev,
        [room]: [...(prev[room] || []), message],
      }));
      setUnreadCounts(prev => ({
        ...prev,
        [room]: activeRoomRef.current === room ? 0 : (prev[room] || 0) + (message.type !== 'system' ? 1 : 0),
      }));
    });

    socket.on('chat-typing', ({ room, userId, username, isTyping }) => {
      setTypingUsers(prev => {
        const roomTyping = { ...(prev[room] || {}) };
        if (isTyping) roomTyping[userId] = username;
        else delete roomTyping[userId];
        return { ...prev, [room]: roomTyping };
      });
    });

    socket.on('presence-update', ({ room, userId, username, status, awayMessage }) => {
      if (!room) return;
      setOnlineUsers(prev => {
        const updated = { ...(prev[room] || {}), [userId]: { username, status, awayMessage } };
        return { ...prev, [room]: updated };
      });
    });

    socket.on('chat-error', ({ message }) => {
      console.warn('Chat error:', message);
    });

    socket.on('connect_error', (err) => {
      console.error('Chat socket error:', err.message);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    axiosInstance.get('/chat/rooms')
      .then(res => {
        setRooms(res.data);
        if (res.data.length > 0 && !activeRoom) {
          const preferred = targetRoom && res.data.find(r => r.name === targetRoom);
          setActiveRoom(preferred ? preferred.name : res.data[0].name);
        }
      })
      .catch(err => console.error('Failed to load rooms:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !activeRoom) return;
    if (joinedRoomsRef.current.has(activeRoom)) return;

    const doJoin = () => {
      socket.emit('join-chat-room', { room: activeRoom });
      joinedRoomsRef.current.add(activeRoom);
      setUnreadCounts(prev => ({ ...prev, [activeRoom]: 0 }));
    };

    if (socket.connected) {
      doJoin();
    } else {
      socket.once('connect', doJoin);
    }
  }, [activeRoom]);

  const handleSendMessage = useCallback(({ content, type, sharedItem }) => {
    const socket = socketRef.current;
    if (!socket || !activeRoom) return;
    socket.emit('send-chat-message', { room: activeRoom, content, type, sharedItem });
  }, [activeRoom]);

  const handleTypingStart = useCallback(() => {
    socketRef.current?.emit('typing-start-room', { room: activeRoom });
  }, [activeRoom]);

  const handleTypingStop = useCallback(() => {
    socketRef.current?.emit('typing-stop-room', { room: activeRoom });
  }, [activeRoom]);

  const handleSetStatus = useCallback((status, awayMessage) => {
    socketRef.current?.emit('set-status', { status, awayMessage });
  }, []);

  const handleSwitchRoom = useCallback((roomName) => {
    setActiveRoom(roomName);
    setUnreadCounts(prev => ({ ...prev, [roomName]: 0 }));
    const socket = socketRef.current;
    if (!socket) return;
    if (!joinedRoomsRef.current.has(roomName)) {
      socket.emit('join-chat-room', { room: roomName });
      joinedRoomsRef.current.add(roomName);
    }
  }, []);

  const handleRoomCreated = useCallback((newRoom) => {
    setRooms(prev => [...prev, newRoom]);
    handleSwitchRoom(newRoom.name);
  }, [handleSwitchRoom]);

  const activeRoomData = rooms.find(r => r.name === activeRoom) || null;
  const activeMessages = messages[activeRoom] || [];
  const activeTyping = typingUsers[activeRoom] || {};
  const activeOnlineUsers = onlineUsers[activeRoom] || {};

  return (
    <>
    <AppBarHeader isAdmin={isAdmin} userId={currentUserId} onLogout={handleLogout} hideCollectionNav={true} />
    <Box className="retro-chat-root" sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      <RoomList
        rooms={rooms}
        activeRoom={activeRoom}
        unreadCounts={unreadCounts}
        onSelectRoom={handleSwitchRoom}
        currentUserId={currentUserId}
        onSetStatus={handleSetStatus}
        onRoomCreated={handleRoomCreated}
      />
      {activeRoomData ? (
        <RoomPanel
          room={activeRoomData}
          messages={activeMessages}
          typingUsers={activeTyping}
          onlineUsers={activeOnlineUsers}
          currentUserId={currentUserId}
          currentUsername={currentUsername}
          onSendMessage={handleSendMessage}
          onTypingStart={handleTypingStart}
          onTypingStop={handleTypingStop}
          onSetStatus={handleSetStatus}
          onJoinRoom={handleSwitchRoom}
        />
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Loading rooms...
          </Typography>
        </Box>
      )}
    </Box>
    </>
  );
}
